import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

function getLiveViewerCount() {
  const now = new Date();
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  const hour = now.getHours();
  const block = Math.floor(now.getMinutes() / 8);
  const seed = ((dayOfYear * 251) + (hour * 53) + (block * 37));
  const hash = (((seed * 2654435761) >>> 0) % 21);
  return hash + 30;
}

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com','yahoo.com','yahoo.co.in','hotmail.com','outlook.com','aol.com',
  'icloud.com','mail.com','protonmail.com','zoho.com','yandex.com','gmx.com',
  'live.com','msn.com','me.com','inbox.com','fastmail.com','tutanota.com',
  'rediffmail.com','mailinator.com','guerrillamail.com','tempmail.com',
  'proton.me','pm.me','hey.com','duck.com',
]);

export default function EmailGate({ onSubmit }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [viewerCount, setViewerCount] = useState(getLiveViewerCount);

  useEffect(() => {
    // Always show email gate on every visit
  }, [onSubmit]);

  useEffect(() => {
    const interval = setInterval(() => {
      setViewerCount(getLiveViewerCount());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }
    const domain = trimmed.split('@')[1];
    if (FREE_EMAIL_DOMAINS.has(domain)) {
      setError('Please use your work email');
      return;
    }
    if (!consent) {
      setError('Please agree to the Privacy Policy');
      return;
    }
    localStorage.setItem('forsyt_email', trimmed);

    // Fire-and-forget tracking, never blocks UX
    const API = process.env.REACT_APP_BACKEND_URL;
    if (API) {
      fetch(`${API}/api/events/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'signup', email: trimmed }),
      }).catch(() => {});
    }

    // Lead captured. Do not enter the sandbox. Just acknowledge.
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center backdrop-blur-[6px] bg-black/30" data-testid="email-gate">
      <div className="w-full max-w-sm mx-4">
        <div className="bg-white rounded-2xl shadow-2xl p-7 border border-gray-200" data-testid="email-gate-card">
          <div className="flex justify-center mb-5">
            <img
              src="/assets/gt687tca.png"
              alt="Forsyt"
              className="h-7 w-auto"
            />
          </div>

          {submitted ? (
            <div className="text-center py-4" data-testid="email-gate-submitted">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                Response taken.
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                Thank you. We will be in touch shortly.
              </p>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 mt-5 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
                data-testid="email-gate-done"
              >
                Back to home
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold text-gray-900 mb-1 text-center">
                Enter your email to explore
              </h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                See how Forsyt gives CROs and reps full pipeline visibility.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder="name@company.com"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    autoFocus
                    data-testid="email-input"
                  />
                  {error && <p className="text-red-500 text-xs mt-1.5" data-testid="email-error">{error}</p>}
                </div>
                <label className="flex items-start gap-2 cursor-pointer" data-testid="consent-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => { setConsent(e.target.checked); setError(''); }}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="consent-checkbox"
                  />
                  <span className="text-xs text-gray-500 leading-relaxed">
                    I agree to the{' '}
                    <Link to="/privacy" className="text-blue-600 underline hover:text-blue-700" target="_blank">Privacy Policy</Link>
                  </span>
                </label>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-all"
                  data-testid="email-submit"
                >
                  Start Exploring
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>

              <div className="flex items-center justify-center gap-2 mt-4" data-testid="live-viewer-count">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="text-xs text-gray-500">{viewerCount} people explored this in the last hour</span>
              </div>

              <p className="text-[11px] text-gray-400 text-center mt-3">
                Sandbox demo with mock data. No real CRM data is accessed.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
